/**
 * Echo Hunt -- live (DB-backed) story lifecycle (P11, §8.3 + §8.4).
 *
 * A story is one pool character's run, spanning several rounds (§14 `echo_stories`). This wrapper is where
 * every write to that row happens, and it is the only place that knows how a round's score turns into a
 * change in the story:
 *
 *  1. **Starting** (`EnsureActiveStory_Live`): a story is now MANDATORY -- §16.2 P11 makes the summary
 *     continuity and the door choice meaningful, and both need a story row to live on. It picks a random
 *     world and a random pool character, excluding the previous story's choice of each (§8.3 "Starts a new
 *     story with a DIFFERENT character").
 *  2. **The door** (`SetDoorVerdict_Live` + `ApplyDoorDecision_Live`): two separate events, because they
 *     answer two separate questions (§11.5 W2). The PLAYER's verdict is authorial and moves nothing -- the
 *     character has not seen the door. The CHARACTER's decision is in-fiction, and only `taken` appends a
 *     world visit. The story's location is DERIVED from that history (`CurrentWorldId`), never stored, so
 *     there is exactly one answer to "where is this story" -- see `Story.ts`'s header.
 *  3. **The meter** (`ApplySuspicionAfterRound_Live`): moves `suspicion` from a finished round's score, and
 *     -- only when applying suspicion is enabled -- writes the lucid interlude and ends the story (§8.4).
 *  4. **Orphan repair** (`AssignStoryCharacter_Live`): a story whose character was deleted cannot be
 *     played; this is the picker P10.5 deferred to P11.
 *  5. **Recovery** (`EnsureStoryWorld_Live`): the world a story was in was deleted from the pool, so it is
 *     moved into a real one and the move is RECORDED rather than substituted silently (§11.5 W6).
 *
 * ## The user-facing opt-out (§8.4)
 *
 * `echoProfiles.suspicion.applied` gates ONLY step 3's effects, never the recording. The caller of
 * `ApplySuspicionAfterRound_Live` always gets the new number; `ended` is the part that can be false
 * because of a setting. See `Story.ts` for the full rationale and the exact wording the UI uses.
 *
 * ## Bails
 *
 * `echoStories` is the one collection in this feature that no tab renders, so a cold read throws a
 * `BailError` that only a React `observer` catches. Every function here that can be reached from a click
 * handler or a `useEffect` wraps its reads in `GetAsync` at the CALL SITE (documented per function),
 * exactly like `Weave_Live`/`Round_Live`. `CatchBail` is deliberately NOT used for story reads: it turns
 * "still loading" into "no story", which would start a second story mid-run and orphan the first.
 *
 * See `Docs/Plans/Others/2026-10-19_EchoHunt.md` §8.3, §8.4, §14, §16.2 P11.
 */
import {GetAsync} from "mobx-graphlink";
import {MeID} from "../Server/DB/users.js";
import {EchoStory, GetEchoStories} from "../Server/DB/echoStories/@EchoStory.js";
import {EchoRound, GetEchoRoundsForStory} from "../Server/DB/echoRounds/@EchoRound.js";
import {AddEchoStory, UpdateEchoStory} from "../Server/Commands/EchoStoryCommands.js";
import {UpdateEchoProfile} from "../Server/Commands/EchoProfileCommands.js";
import {UpdateEchoRound} from "../Server/Commands/EchoRoundCommands.js";
import {GetMyEchoProfile, GetSuspicionSettings} from "./Profiles_Live.js";
import {GetCharacterPool_Live, ResolveStoryCharacter_Live} from "./Characters_Live.js";
import {GetActiveStory_Live, GetPreviousEndedStory_Live, GetWorldPool_Live} from "./Weave_Live.js";
import {GetCheckOptionsFromProfile} from "./Bullets_Live.js";
import {liveLLMBackend} from "./LLM_Live.js";
import {BuildFallbackInterlude, WriteInterlude} from "./Interlude.js";
import {PickRandomCharacter, PickRandomWorld} from "./Worlds.js";
import {
	AppendWorldVisit,
	CanAnswerDoor,
	ClampSuspicion,
	ComputeSuspicionChange,
	CurrentWorldId,
	DescribeDoorBlock,
	DescribeSuspicion,
	DoorState,
	GetAccumulatedSuspicion,
	GetDoorState,
	GetEffectiveSuspicion,
	GetStoryPlayability,
	GetSuspicionAdjustment,
	HasSuspicionAdjustment,
	HasUsableSummary,
	IsStoryEnded,
	IsStoryEndedManually,
	PendingWorldTransition,
	PreviousCharacterIdToExclude,
	ResolveDoorDestination,
	StoryPlayability,
	ThresholdIsClamped,
	WorldNameFromHistory,
} from "./Story.js";
import {DoorVerdict, EchoCharacter, EchoWorld, WorldVisit} from "./Types.js";
import type {DoorDecision} from "./Types.js";

// reads
// ==========

/** The stories the signed-in user has, newest first. */
export function GetMyEchoStories_Live(): EchoStory[] {
	const userID = MeID();
	if (userID == null) return [];
	return [...GetEchoStories(userID)].sort((a, b)=>b.createdAt - a.createdAt);
}

/** The running story, or null. Re-exported from `Weave_Live` because almost every P11 caller wants it and
 * importing two modules to ask one question is noise. */
export {GetActiveStory_Live};

/** The rounds of one story, oldest first. Safe from a React render (the Play tab already subscribes). */
export function GetStoryRounds_Live(storyId: string|n): EchoRound[] {
	if (storyId == null) return [];
	return [...GetEchoRoundsForStory(storyId)];
}

/** Whether the story can be played right now. Takes the story, so the panel can ask about a row it shows. */
export function GetStoryPlayability_Live(story: EchoStory|n): StoryPlayability {
	return GetStoryPlayability(story, ResolveStoryCharacter_Live(story));
}

/** The world the story is currently set in (§11.5 W1). Derived from `worldHistory`, falling back to the
 * newest round's world for a pre-11.5 story, so no existing story becomes unplayable.
 *
 * Null when the pool entry no longer exists. Null is not fatal: round generation RECOVERS the story into a
 * real world before weaving (`EnsureStoryWorld_Live`), so the character and the summary are never lost to a
 * deleted entry. */
export function GetStoryWorld_Live(story: EchoStory|n): EchoWorld|n {
	const worldId = CurrentWorldId(story, story != null ? GetStoryRounds_Live(story.id) : null);
	if (worldId == null) return null;
	return GetWorldPool_Live().find(a=>a.id == worldId) ?? null;
}

/** The id the story's location resolves to, whether or not the pool still contains it. Used by the UI to
 * say "the world you were in was removed" instead of showing nothing. */
export function GetStoryWorldId_Live(story: EchoStory|n): string|null {
	return CurrentWorldId(story, story != null ? GetStoryRounds_Live(story.id) : null);
}

/** A name for the story's current location that survives the world being deleted from the pool: the pool
 * entry's name when it still exists, else the name the history recorded when the character arrived. */
export function GetStoryWorldName_Live(story: EchoStory|n): string|null {
	const world = GetStoryWorld_Live(story);
	if (world != null) return world.name;
	return WorldNameFromHistory(story, GetStoryWorldId_Live(story));
}

/** The character the story follows, resolved from the pool (§8.7). Null when it was deleted. */
export function GetStoryCharacter_Live(story: EchoStory|n): EchoCharacter|n {
	return ResolveStoryCharacter_Live(story);
}

/** The story's suspicion readout, in the words every panel uses (`Story.DescribeSuspicion` plus the
 * threshold clamp warning). Kept here rather than in the panel so the reveal, the story tab and the
 * settings panel cannot describe the same number differently. */
export function DescribeStorySuspicion_Live(story: EchoStory|n): string {
	return DescribeSuspicion(GetEffectiveSuspicion(story), GetSuspicionSettings());
}

// Re-exported for the UI, which should never import the pure module just to format a number.
export {DescribeSuspicion};

/** True when the profile's stored threshold is being clamped, so the settings panel can say the meter is
 * not honouring the number shown. */
export function SuspicionThresholdIsClamped_Live(): boolean {
	const profile = GetMyEchoProfile();
	return ThresholdIsClamped(profile?.suspicion);
}

// starting a story (§8.3)
// ==========

export type StartStoryResult =
	| {ok: true, story: EchoStory}
	| {ok: false, error: string};

/** Which story is SELECTED (stored on the profile, so it survives a reload). Refuses a story that has ended. */
export async function SetActiveStory_Live(storyId: string): Promise<boolean> {
	const profile = await GetAsync(()=>GetMyEchoProfile());
	if (profile == null) return false;

	const story = await GetAsync(()=>GetMyEchoStories_Live().find(a=>a.id == storyId) ?? null);
	if (story == null || IsStoryEnded(story)) return false;

	await new UpdateEchoProfile({id: profile.id, updates: {activeStoryId: storyId}}).RunOnServer();
	return true;
}

/** Starts a NEW story and selects it. Always creates -- the round path uses `EnsureActiveStory_Live`, which
 * must NOT open a second run alongside a running one. The new story avoids the selected story's picks. */
export async function CreateStory_Live(options?: {rng?: () => number}): Promise<StartStoryResult> {
	const rng = options?.rng ?? Math.random;

	const profile = await GetAsync(()=>GetMyEchoProfile());
	if (profile == null) return {ok: false, error: "Create an Echo Hunt profile (Settings tab) to start a story."};

	const worldPool = profile.worldPool ?? [];
	if (worldPool.length == 0) {
		return {ok: false, error: "No worlds in your pool -- add one on the Worlds tab before starting a story."};
	}
	const characterPool = profile.characters ?? [];
	if (characterPool.length == 0) {
		return {ok: false, error: "No characters in your pool -- add one on the Characters tab before starting a story."};
	}

	// whatever we are about to start must not resemble the run the user is already in (or just finished)
	const selected = await GetAsync(()=>GetActiveStory_Live());
	const previous = selected ?? await GetAsync(()=>GetPreviousEndedStory_Live());

	// §8.3: a new story starts with a DIFFERENT character (and in a different world) from the last one.
	// §11.5 W1: "the last one's world" now means the world it STARTED in (`history[0]`), not where it ended
	// up -- two characters in a row should not open in the same place, whatever happened along the way.
	const character = PickRandomCharacter(characterPool, rng, PreviousCharacterIdToExclude(previous));
	if (character == null) {
		return {ok: false, error: "No characters in your pool -- add one on the Characters tab before starting a story."};
	}
	const previousStart = previous != null ? CurrentWorldId({worldHistory: (previous.worldHistory ?? []).slice(0, 1)}) : null;
	const world = PickRandomWorld(worldPool, rng, previousStart) ?? worldPool[0];

	const story = new EchoStory({
		characterId: character.id,
		summary: null,
		suspicionAccumulated: 0,
		suspicionUserAdjustment: 0,
		// §11.5 W1: the location IS the history, and this is its first entry. There is no `worldId` field.
		worldHistory: AppendWorldVisit([], world, 0),
		ended: false,
		lucidInterlude: null,
	});

	const id = (await new AddEchoStory({entry: story}).RunOnServer()).id;
	// select it, so "start a story" means "and switch to it" -- otherwise the button would create a story the
	// user then has to go and find in the switcher
	await new UpdateEchoProfile({id: profile.id, updates: {activeStoryId: id}}).RunOnServer();

	return {ok: true, story: new EchoStory({...story, id})};
}

/** Returns the selected running story, starting one if there is none. Idempotent: building a round must NOT
 * open a second run, which is `CreateStory_Live`'s job. */
export async function EnsureActiveStory_Live(options?: {rng?: () => number}): Promise<StartStoryResult> {
	// wrapped: called from click handlers and `useEffect`s, where nothing catches a cold-read bail
	const existing = await GetAsync(()=>GetActiveStory_Live());
	if (existing != null) return {ok: true, story: existing};

	return CreateStory_Live(options);
}

/** Assigns a character to a story that has none, or whose character was deleted. Does NOT reset the summary,
 * suspicion or world -- repairing an orphan means "continue this story, with this person". */
export async function AssignStoryCharacter_Live(storyId: string, characterId: string): Promise<boolean> {
	const story = await GetAsync(()=>GetMyEchoStories_Live().find(a=>a.id == storyId) ?? null);
	if (story == null) return false;
	if (GetCharacterPool_Live().every(a=>a.id != characterId)) return false;

	await new UpdateEchoStory({id: storyId, updates: {characterId}}).RunOnServer();
	return true;
}

/** Moves a story into a different world WITHOUT a door -- the impatient lever (§11.5 W5).
 *
 * This is the author overriding the fiction, so it is deliberately distinct from a door: it is recorded
 * with `cause: "manual"`, and it refuses while a door is in play. Otherwise a manual move plus a door
 * answer would compound into two jumps with no record of which one the player meant. */
export async function SetStoryWorld_Live(storyId: string, worldId: string): Promise<{ok: true}|{ok: false, error: string}> {
	const story = await GetAsync(()=>GetMyEchoStories_Live().find(a=>a.id == storyId) ?? null);
	if (story == null) return {ok: false, error: "That story no longer exists."};

	const world = GetWorldPool_Live().find(a=>a.id == worldId) ?? null;
	if (world == null) return {ok: false, error: "That world is no longer in your pool."};

	// §11.5 W5: a door in play means the story's next move is already spoken for
	if (GetDoorState_Live(story) != null) {
		return {ok: false, error: "A door is in play on this story. Answer or play it out before moving the story by hand."};
	}

	const rounds = GetStoryRounds_Live(storyId);
	if (CurrentWorldId(story, rounds) == world.id) return {ok: true}; // already there -- nothing to record

	await new UpdateEchoStory({id: storyId, updates: {
		worldHistory: AppendWorldVisit(story.worldHistory, world, rounds.length, {cause: "manual"}),
	}}).RunOnServer();
	return {ok: true};
}

/** Ends a story manually (`StoryEndReason` `manual`). Used by the "end this story" button, which is the
 * user's counterpart to the meter boiling over -- and the only way to change character without waiting for
 * a lucid ending. */
export async function EndStory_Live(storyId: string): Promise<boolean> {
	const story = await GetAsync(()=>GetMyEchoStories_Live().find(a=>a.id == storyId) ?? null);
	if (story == null) return false;
	await new UpdateEchoStory({id: storyId, updates: {ended: true}}).RunOnServer();
	return true;
}

/** True when a story was ended by hand rather than by the character waking up (`Story.IsStoryEndedManually`,
 * re-exported so the UI does not need both modules). */
export {IsStoryEndedManually};

/** Re-opens a story ended BY HAND. Refuses a lucid ending: its interlude is prose the player has already read.
 * Leaves `suspicion` alone, so if it is past the threshold the next round re-crosses and ends the run then. */
export async function ReopenStory_Live(storyId: string): Promise<boolean> {
	const story = await GetAsync(()=>GetMyEchoStories_Live().find(a=>a.id == storyId) ?? null);
	if (story == null) return false;
	if (!IsStoryEndedManually(story)) return false;

	await new UpdateEchoStory({id: storyId, updates: {ended: false}}).RunOnServer();
	return true;
}

/** Sets the hand adjustment so the EFFECTIVE value becomes `target` (0..1), leaving the story's own
 * accumulated value untouched -- which is what makes the adjustment reversible (`ResetStorySuspicion_Live`). */
export async function SetStorySuspicion_Live(storyId: string, target: number): Promise<boolean> {
	const story = await GetAsync(()=>GetMyEchoStories_Live().find(a=>a.id == storyId) ?? null);
	if (story == null) return false;

	const clamped = ClampSuspicion(target);
	const adjustment = Math.round((clamped - GetAccumulatedSuspicion(story)) * 100) / 100;
	await new UpdateEchoStory({id: storyId, updates: {suspicionUserAdjustment: adjustment}}).RunOnServer();
	return true;
}

/** Drops the hand adjustment, so the meter goes back to exactly what the story accumulated. */
export async function ResetStorySuspicion_Live(storyId: string): Promise<boolean> {
	const story = await GetAsync(()=>GetMyEchoStories_Live().find(a=>a.id == storyId) ?? null);
	if (story == null) return false;

	await new UpdateEchoStory({id: storyId, updates: {suspicionUserAdjustment: null}}).RunOnServer();
	return true;
}

/** True when a hand adjustment is in effect, so the UI can offer to reset it. */
export function HasSuspicionAdjustment_Live(story: EchoStory|n): boolean {
	return HasSuspicionAdjustment(story);
}

// the door (§8.3, §11.5 W2) -- three actors, three questions
// ==========

/** The story's live door, or null when there is none (§11.5 W5). Derived from the ROUNDS, never stored: a
 * story-level mirror is exactly how the old `doorPending` flag went stale. */
export function GetDoorState_Live(story: EchoStory|n): DoorState|null {
	if (story == null) return null;
	return GetDoorState(GetStoryRounds_Live(story.id));
}

/** True when the next round should PRESENT a staged door and ask the CHARACTER to decide (§11.5 W2). */
export function GetStagedDoor_Live(story: EchoStory|n): {roundId: string, description: string}|null {
	const state = GetDoorState_Live(story);
	if (state == null || !state.awaitingCharacter) return null;
	return {roundId: state.roundId, description: state.offer.description};
}

export type DoorVerdictResult =
	| {ok: true, verdict: DoorVerdict}
	| {ok: false, error: string};

/** Records the PLAYER's verdict on a door (§11.5 W2). Authorial, outside the fiction, and it MOVES NOTHING:
 * the character has not seen the door yet, and the world changes only if they take it in a later round.
 *
 * Every refusal comes from the pure gate `CanAnswerDoor`, so the reveal and the story tab cannot disagree
 * about whether a door is answerable (the old single `ResolveDoor_Live` had no such check at all). */
export async function SetDoorVerdict_Live(options: {storyId: string, roundId: string, verdict: DoorVerdict}): Promise<DoorVerdictResult> {
	const {storyId, roundId, verdict} = options;

	const story = await GetAsync(()=>GetMyEchoStories_Live().find(a=>a.id == storyId) ?? null);
	if (story == null) return {ok: false, error: "That story no longer exists."};

	const rounds = GetStoryRounds_Live(storyId);
	const round = rounds.find(a=>a.id == roundId) ?? null;
	if (round == null) return {ok: false, error: "That round is not part of this story."};

	const gate = CanAnswerDoor(story, round, rounds);
	if (!gate.ok) return {ok: false, error: DescribeDoorBlock(gate.reason)};

	await new UpdateEchoRound({id: roundId, updates: {doorVerdict: verdict}}).RunOnServer();
	return {ok: true, verdict};
}

/** Applies the CHARACTER's decision from a finished weave (§11.5 W2). Called by round generation, inside
 * the same flow that saved the round.
 *
 * `taken` appends a world visit, stamped with the position of the round AFTER the deciding one -- the new
 * world begins with the next installment, and the arrival is narrated there (finding #8: the stamp used to
 * be the round count at answer time, which mis-attributed a late answer). */
export async function ApplyDoorDecision_Live(options: {
	storyId: string,
	roundId: string,
	decision: DoorDecision,
	offer: {description: string, targetWorldId?: string|n},
	rng?: () => number,
}): Promise<{moved: boolean, worldId: string|null, worldName: string|null, fallback: boolean}> {
	const {storyId, roundId, decision, offer} = options;
	const rng = options.rng ?? Math.random;

	const story = await GetAsync(()=>GetMyEchoStories_Live().find(a=>a.id == storyId) ?? null);
	if (story == null) return {moved: false, worldId: null, worldName: null, fallback: false};

	await new UpdateEchoRound({id: roundId, updates: {doorDecision: decision}}).RunOnServer();
	if (decision != "taken") return {moved: false, worldId: null, worldName: null, fallback: false};

	const rounds = GetStoryRounds_Live(storyId);
	const decidingIndex = rounds.findIndex(a=>a.id == roundId);
	const destination = ResolveDoorDestination({
		offer,
		currentWorldId: CurrentWorldId(story, rounds),
		pool: GetWorldPool_Live(),
		rng,
	});
	if (destination.worldId == null) return {moved: false, worldId: null, worldName: null, fallback: true};

	// the arrival belongs to the round AFTER the one that made the decision
	const arrivalIndex = (decidingIndex >= 0 ? decidingIndex : rounds.length - 1) + 1;
	await new UpdateEchoStory({id: storyId, updates: {
		worldHistory: AppendWorldVisit(
			story.worldHistory,
			{id: destination.worldId, name: destination.worldName},
			arrivalIndex,
			{cause: "door", doorDescription: offer.description},
		),
	}}).RunOnServer();
	await new UpdateEchoRound({id: roundId, updates: {
		doorResolvedWorldId: destination.worldId,
		doorResolvedAt: Date.now(),
	}}).RunOnServer();

	return {moved: true, worldId: destination.worldId, worldName: destination.worldName, fallback: destination.fallback};
}

/** Recovers a story whose world was deleted from the pool (§11.5 W6): moves it into a real world and
 * RECORDS the move, so the history stays an honest account and the weaver can be told.
 *
 * Called by round generation before context assembly. Without it, `GetWeaveContext_Live` silently drew a
 * different random world per round while the story's own record said nothing (finding #5). */
export async function EnsureStoryWorld_Live(storyId: string|n, options?: {rng?: () => number}): Promise<{moved: boolean, fromName: string|null, toWorld: EchoWorld|null}> {
	if (storyId == null) return {moved: false, fromName: null, toWorld: null};
	const rng = options?.rng ?? Math.random;

	const story = await GetAsync(()=>GetMyEchoStories_Live().find(a=>a.id == storyId) ?? null);
	if (story == null) return {moved: false, fromName: null, toWorld: null};

	const rounds = GetStoryRounds_Live(storyId);
	const currentId = CurrentWorldId(story, rounds);
	const pool = GetWorldPool_Live();
	const current = pool.find(a=>a.id == currentId) ?? null;
	if (current != null) return {moved: false, fromName: null, toWorld: current};

	// the world is gone: pick a replacement that is not the dead id, and say what was lost by name
	const fromName = WorldNameFromHistory(story, currentId);
	const replacement = PickRandomWorld(pool, rng, currentId);
	if (replacement == null) return {moved: false, fromName, toWorld: null};

	await new UpdateEchoStory({id: storyId, updates: {
		worldHistory: AppendWorldVisit(story.worldHistory, replacement, rounds.length, {cause: "recovery"}),
	}}).RunOnServer();
	return {moved: true, fromName, toWorld: replacement};
}

/** Asks the NEXT round's weave for a way out (§8.3 override). Under §11.5 W2 the player pressing this has
 * ALREADY made the authorial decision, so the override stages a door directly and skips the proposal step:
 * the character still decides whether to use it. One-shot. */
export async function RequestDoorOverride_Live(storyId: string): Promise<boolean> {
	const story = await GetAsync(()=>GetMyEchoStories_Live().find(a=>a.id == storyId) ?? null);
	if (story == null) return false;
	if (IsStoryEnded(story)) return false;

	await new UpdateEchoStory({id: storyId, updates: {doorOverrideRequested: true}}).RunOnServer();
	return true;
}

/** Clears the one-shot override flag once a round has been woven with it. Called by the round generator so
 * that the request applies to exactly ONE installment -- otherwise every subsequent round would insist on
 * a door and the story could never settle anywhere. */
export async function ClearDoorOverride_Live(storyId: string|n): Promise<void> {
	if (storyId == null) return;
	await new UpdateEchoStory({id: storyId, updates: {doorOverrideRequested: false}}).RunOnServer();
}

/** True when the next round has been asked to produce a door. Read by the round generator and shown on the
 * story tab so the pending request is visible instead of implied. */
export function IsDoorOverrideRequested(story: EchoStory|n): boolean {
	return story?.doorOverrideRequested == true;
}

// the suspicion meter (§8.4)
// ==========

export type SuspicionApplicationResult = {
	/** the meter before and after this round */
	before: number;
	after: number;
	/** whether the settings let the meter act on the story at all */
	applied: boolean;
	/** true when the meter crossed AND applying it is enabled -- the story is now over */
	ended: boolean;
	/** the narrated interlude, when the story ended because of suspicion */
	interlude?: string|null;
	/** true when the interlude is the written fallback rather than the model's passage */
	interludeIsFallback?: boolean;
	/** why the model path failed, when it did */
	interludeError?: string|null;
};

/** Moves suspicion by one finished round, and ends the story if that is both crossed and enabled. Called at
 * SUBMIT, from the score, so it depends only on rounds actually played. */
export async function ApplySuspicionAfterRound_Live(options: {storyId: string, hits: number, misses: number}): Promise<SuspicionApplicationResult|null> {
	const {storyId, hits, misses} = options;
	const story = await GetAsync(()=>GetMyEchoStories_Live().find(a=>a.id == storyId) ?? null);
	if (story == null) return null;

	const settings = GetSuspicionSettings();
	// only the ACCUMULATED value moves here; the user's hand adjustment is left exactly as they set it
	const update = ComputeSuspicionChange(GetAccumulatedSuspicion(story), {hits, misses}, settings, GetSuspicionAdjustment(story));

	// a story that is already over keeps its final number: recomputing it from a stored answer sheet (e.g.
	// re-opening a revealed round) must not move a meter whose ending has already been narrated
	if (IsStoryEnded(story)) {
		return {before: update.before, after: update.before, applied: settings.applied, ended: true};
	}

	if (!update.crossed) {
		await new UpdateEchoStory({id: storyId, updates: {suspicionAccumulated: update.after}}).RunOnServer();
		return {before: update.before, after: GetEffectiveSuspicion({suspicionAccumulated: update.after, suspicionUserAdjustment: GetSuspicionAdjustment(story)}), applied: settings.applied, ended: false};
	}

	// crossed, and applying is enabled -- write the interlude and close the run
	const world = GetStoryWorld_Live(story);
	const character = GetStoryCharacter_Live(story);
	const profile = await GetAsync(()=>GetMyEchoProfile());

	let text: string;
	let isFallback = false;
	let error: string|n = null;

	if (world == null || character == null) {
		// the story's world or character was deleted while it ran. The interlude is narration IN the story,
		// so without them there is nothing to narrate from -- and inventing a stand-in would describe a
		// different story's ending. The fallback says what happened without pretending.
		text = BuildFallbackInterlude({characterName: character?.name ?? "The character"});
		isFallback = true;
		error = "The story's world or character is no longer available, so the interlude could not be written.";
	} else {
		const result = await WriteInterlude(liveLLMBackend, {
			world,
			character,
			summary: story.summary,
			suspicion: GetEffectiveSuspicion({suspicionAccumulated: update.after, suspicionUserAdjustment: GetSuspicionAdjustment(story)}),
			model: profile?.modelRoles?.narrator ?? null,
			checkOptions: await GetAsync(()=>GetCheckOptionsFromProfile()),
		});
		text = result.text;
		isFallback = !result.generatedByModel;
		error = result.error ?? null;
	}

	await new UpdateEchoStory({id: storyId, updates: {
		suspicionAccumulated: update.after,
		ended: true,
		lucidInterlude: text,
		doorOverrideRequested: false,
	}}).RunOnServer();

	return {
		before: update.before,
		after: GetEffectiveSuspicion({suspicionAccumulated: update.after, suspicionUserAdjustment: GetSuspicionAdjustment(story)}),
		applied: settings.applied,
		ended: true,
		interlude: text,
		interludeIsFallback: isFallback,
		interludeError: error,
	};
}

/** Rewrites the interlude text by hand. The narration is the one artifact of a story the user may well want
 * to fix -- and re-running the model to change one sentence costs tokens and produces a different ending. */
export async function SetInterlude_Live(storyId: string, interlude: string|null): Promise<boolean> {
	const story = await GetAsync(()=>GetMyEchoStories_Live().find(a=>a.id == storyId) ?? null);
	if (story == null) return false;
	const trimmed = interlude?.trim() ?? "";
	await new UpdateEchoStory({id: storyId, updates: {lucidInterlude: trimmed.length > 0 ? trimmed : null}}).RunOnServer();
	return true;
}

// story summary (§8.1, §8.3)
// ==========

/** Writes the weaver's `summaryOut` back to the story (§8.1). A no-op for an empty summary: `HasUsableSummary`
 * is the single test for "is this a summary", so a whitespace-only reply cannot silently restart the story's
 * continuity. */
export async function AdvanceStorySummary_Live(storyId: string|null, summaryOut: string|null): Promise<void> {
	if (storyId == null || !HasUsableSummary(summaryOut)) return;
	await new UpdateEchoStory({id: storyId, updates: {summary: summaryOut}}).RunOnServer();
}

/** The world-visit history of a story, for the story tab (§14). Returns an empty list for a pre-P11 story,
 * which simply has no recorded history rather than an invented one. */
export function GetStoryWorldHistory_Live(story: EchoStory|n): WorldVisit[] {
	return [...(story?.worldHistory ?? [])];
}

/** The move the next round must narrate an arrival for, or null (§11.5 W4). Derived from the history, so a
 * round that has already been written in the new world clears it by construction -- no flag to reset. */
export function GetPendingWorldTransition_Live(story: EchoStory|n) {
	if (story == null) return null;
	return PendingWorldTransition(story, GetStoryRounds_Live(story.id).length);
}
